/**
 * The floating toolbar over an in-place screenshot markup (MarkupEditor): tools, pen
 * colours, Undo / Redo / Delete / Clear all, the notes list, a button that moves the bar
 * between the top and bottom of the screen (so it never hides what is being marked), and
 * Cancel / Done. It floats over the frozen screen rather than framing it in a window.
 */

import {
  ArrowDownToLine,
  ArrowUpRight,
  ArrowUpToLine,
  Eraser,
  MousePointer2,
  Redo2,
  Square,
  SquareSlash,
  StickyNote,
  Trash2,
  Type,
  Undo2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  MARK_COLOR_LABELS,
  MARK_COLOR_TOKENS,
  MARK_COLORS,
  type MarkColor,
  type MarkTool,
} from "@/lib/markup/marks";
import { cn } from "@/lib/utils";

export const MARKUP_TOOLS: Array<{
  tool: MarkTool;
  label: string;
  icon: typeof Square;
  hint: string;
}> = [
  {
    tool: "select",
    label: "Select",
    icon: MousePointer2,
    hint: "Select a mark to move, recolour or delete it",
  },
  { tool: "box", label: "Box", icon: Square, hint: "Drag to draw a box" },
  {
    tool: "arrow",
    label: "Arrow",
    icon: ArrowUpRight,
    hint: "Drag from where the arrow starts to what it points at",
  },
  { tool: "text", label: "Text", icon: Type, hint: "Click where the note goes, then type it" },
  {
    tool: "redact",
    label: "Redact",
    icon: SquareSlash,
    hint: "Drag to black out anything private — the unmarked image is then never kept",
  },
];

export type ToolbarPosition = "top" | "bottom";

export function penVar(color: MarkColor): string {
  return `var(${MARK_COLOR_TOKENS[color]})`;
}

interface MarkupToolbarProps {
  tool: MarkTool;
  onTool: (tool: MarkTool) => void;
  color: MarkColor;
  onColor: (color: MarkColor) => void;
  history: { canUndo: boolean; canRedo: boolean; hasMarks: boolean; hasSelection: boolean };
  onUndo: () => void;
  onRedo: () => void;
  onDelete: () => void;
  onClear: () => void;
  notes: { count: number; open: boolean; onToggle: () => void };
  position: ToolbarPosition;
  onTogglePosition: () => void;
  onCancel: () => void;
  onDone: () => void;
}

function Divider() {
  return <span aria-hidden className="mx-0.5 hidden h-5 w-px shrink-0 bg-border sm:block" />;
}

export function MarkupToolbar({
  tool,
  onTool,
  color,
  onColor,
  history,
  onUndo,
  onRedo,
  onDelete,
  onClear,
  notes,
  position,
  onTogglePosition,
  onCancel,
  onDone,
}: MarkupToolbarProps) {
  return (
    <div className="flex max-w-[calc(100vw-1rem)] flex-wrap items-center justify-center gap-1 rounded-xl border border-border bg-card p-1.5 text-card-foreground shadow-lg">
      <div role="toolbar" aria-label="Markup tools" className="flex shrink-0 items-center gap-0.5">
        {MARKUP_TOOLS.map((t) => (
          <Button
            key={t.tool}
            type="button"
            size="sm"
            variant={tool === t.tool ? "default" : "ghost"}
            aria-pressed={tool === t.tool}
            aria-label={t.label}
            title={t.hint}
            onClick={() => onTool(t.tool)}
          >
            <t.icon className="size-4" />
            <span className="hidden lg:inline">{t.label}</span>
          </Button>
        ))}
      </div>

      <Divider />

      <fieldset className="flex shrink-0 items-center gap-1 px-0.5">
        <legend className="sr-only">Colour</legend>
        {MARK_COLORS.map((c) => (
          <label
            key={c}
            title={MARK_COLOR_LABELS[c]}
            className={cn(
              "size-6 cursor-pointer rounded-full border-2 border-background shadow-xs outline-offset-1 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
              color === c && "outline-2 outline-ring",
            )}
            style={{ background: penVar(c) }}
          >
            <input
              type="radio"
              name="markup-colour"
              value={c}
              checked={color === c}
              onChange={() => onColor(c)}
              aria-label={MARK_COLOR_LABELS[c]}
              className="sr-only"
            />
          </label>
        ))}
      </fieldset>

      <Divider />

      <div className="flex shrink-0 items-center gap-0.5">
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          aria-label="Undo"
          title="Undo (Ctrl/Cmd+Z)"
          disabled={!history.canUndo}
          onClick={onUndo}
        >
          <Undo2 className="size-4" />
        </Button>
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          aria-label="Redo"
          title="Redo (Shift+Ctrl/Cmd+Z)"
          disabled={!history.canRedo}
          onClick={onRedo}
        >
          <Redo2 className="size-4" />
        </Button>
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          aria-label="Delete the selected mark"
          title="Delete the selected mark (Delete)"
          disabled={!history.hasSelection}
          onClick={onDelete}
        >
          <Trash2 className="size-4" />
        </Button>
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          aria-label="Clear all marks"
          title="Remove every mark (you can undo this)"
          disabled={!history.hasMarks}
          onClick={onClear}
        >
          <Eraser className="size-4" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant={notes.open ? "secondary" : "ghost"}
          aria-expanded={notes.open}
          aria-label={`Notes on the image (${notes.count})`}
          title="Read and edit the notes on the image"
          onClick={notes.onToggle}
        >
          <StickyNote className="size-4" />
          <span className="tabular-nums">{notes.count}</span>
        </Button>
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          aria-label={position === "top" ? "Move the toolbar down" : "Move the toolbar up"}
          title={position === "top" ? "Move the toolbar down" : "Move the toolbar up"}
          onClick={onTogglePosition}
        >
          {position === "top" ? (
            <ArrowDownToLine className="size-4" />
          ) : (
            <ArrowUpToLine className="size-4" />
          )}
        </Button>
      </div>

      <Divider />

      <div className="flex shrink-0 items-center gap-1">
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" size="sm" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}
